import { Store } from '@ngxs/store';
import { EngineService } from 'src/app/engine/engine.service';
import { Procedure } from '../define-procedure';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { PickModel } from 'src/app/engine/models/pick.model';
import { MessageEnum } from '../models/message.enum';
import { HitModel } from 'src/app/engine/models/hit.model';
import { EngineState } from 'src/app/engine/state/engine.state';
import { RPCEvent } from '../define-event';
import { ContentType } from 'src/app/story/models/content-type.enum';
import { LoadStory } from 'src/app/story/state/story.actions';
import { StoryState } from 'src/app/story/state/story.state';
import { PaintService } from 'src/app/engine/paint.service';
import { SceneIO } from '../models/scene.io';
import { CameraService } from 'src/app/engine/camera.service';
import { StoryLabelsState } from 'src/app/story/state/labels/story.labels.state';
import { Observable } from 'rxjs';
import { SetCamera, ZoomCameraStatic } from 'src/app/engine/state/camera/engine.camera.actions';
import { SetLabels } from 'src/app/story/state/labels/story.labels.actions';
import { first } from 'rxjs/operators';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import {
  SetChapterBackground,
  SelectChapterById,
} from 'src/app/story/state/chapters/story.chapters.actions';
import { BdGraphService } from 'src/lib/graph/graph.service';
import { ViewActions } from 'src/app/view/state/view.actions';
import { FlyToObject, SetActiveTool, SetXrayMode } from 'src/app/engine/state/engine.actions';
import { ToolEnum } from 'src/app/engine/models/tool.enum';
import { ViewState } from 'src/app/view/state/view.state';
import { _3DUtils } from 'src/app/shared/libraries/3d-utils/3d-utils';
import { appDefaultBackground } from 'src/app/dialogs/components/background-color-dialog/background-config';
import { SceneAPIUtils } from './scene-utils';
import { disableXrayMode } from 'src/app/shared/xray-mode';
import { NodeActions } from 'src/app/engine/state/nodes/engine.nodes.actions';
import { EngineColorRecipeModel } from 'src/app/edit/models/engine-color-recipe.model';
import { ColorRecipeModel } from 'src/app/edit/models/color-recipe.model';
import { EngineNodesState } from 'src/app/engine/state/nodes/engine.nodes.state';
import { ObjectId, NodeState } from 'src/app/engine/state/nodes/engine.nodes.state.model';
import { PaintPresetOriginal } from 'src/app/engine/models/paint-presets.const';
import { AnimationService } from 'src/app/engine/animation.service';
import { ViewService } from 'src/app/view/view.service';
import { VisibilityLayersService } from 'src/app/story/visibility-layers.service';
import { HumanInterface } from '../../engine/models/human.interface';

function formatPickResult(hit: HitModel): SceneIO.ScenePickResponse {
  if (!hit) {
    return null;
  }

  const { objectId, canvasPos, worldPos } = hit;

  return {
    objectId: objectId ? objectId : undefined,
    canvasPosition: canvasPos
      ? {
          x: canvasPos[0],
          y: canvasPos[1],
        }
      : undefined,
    mode: 'query',
    position: worldPos
      ? {
          x: worldPos[0],
          y: worldPos[1],
          z: worldPos[2],
        }
      : undefined,
  };
}

function isParentSelected(
  Human: HumanInterface,
  nodes: Record<ObjectId, NodeState>,
  objectId: ObjectId,
) {
  const parent = Human.scene.objects[objectId].parent;

  if (parent === null) {
    return false;
  }

  return nodes[parent.objectId].checked || isParentSelected(Human, nodes, parent.objectId);
}

function buildSceneInfoResponse(
  Human: HumanInterface,
  storyDisplayName: string,
  objectIds: string[],
  store: Store,
): SceneIO.SceneInfoResponse {
  const nodes = store.selectSnapshot(EngineNodesState.getNodes);

  const humanObjectInfo = (objectId) => {
    const humanObject = Human.scene.objects[objectId];

    // if the object isn't a leaf, don't include a selected property for it!
    const selected = humanObject.isLeaf()
      ? nodes[objectId].checked || isParentSelected(Human, nodes, objectId)
      : undefined;

    // FIXME: Heads up for future readers:
    // The postmessage layer (Human API) is filtering properties that are undefined.
    // So even though the key is present in this function's return val,
    // it's being stripped before the client gets it.
    // We will not stand for censorship! Free Hong Kong!

    return {
      objectId: humanObject.objectId,
      name: humanObject.displayName,
      description: humanObject.description || '',
      shown: humanObject.shown,
      selected: selected,
      children: humanObject.objects.map((object) => object.objectId),
      parent: humanObject.parent ? humanObject.parent.objectId : null,
    };
  };

  const objects = objectIds.reduce((acc, objectId) => {
    acc[objectId] = humanObjectInfo(objectId);
    return acc;
  }, {});

  return {
    name: storyDisplayName,
    objects,
  };
}

function loadStory(store: Store, storyId: string) {
  const type: ContentType = storyId.includes('/') ? ContentType.Module : ContentType.Bookmark;
  store.dispatch(new LoadStory(storyId, type));
}

function captureScene(
  Human: HumanInterface,
  store: Store,
  cameraService: CameraService,
): SceneIO.SceneCapture {
  // wrangle state from the store or engine
  const camera = cameraService.captureCamera();

  const nodes = store.selectSnapshot(EngineNodesState.getNodes);
  const objects: SceneIO.ObjectCaptureInfo[] = Object.keys(nodes).map((objectId) => {
    return {
      objectId,
      selected: nodes[objectId].checked || isParentSelected(Human, nodes, objectId),
      hidden: !nodes[objectId].shown,
    };
  });

  const background = store.selectSnapshot(StoryChaptersState.getStoryChapters).map((chapter) => {
    return {
      chapterId: chapter.id,
      background: chapter.background || appDefaultBackground,
    };
  });

  // no transformations necessary for this capture data
  const storyId = store.selectSnapshot(StoryState.getStoryId);
  const labels = store.selectSnapshot(StoryLabelsState.getLabels);
  const activeChapterId = store.selectSnapshot(StoryChaptersState.getSelectedChapterId);

  const sceneCapture: SceneIO.SceneCaptureResponse = {
    camera,
    labels,
    storyId,
    objects,
    background,
    activeChapterId,
  };

  return sceneCapture;
}

function applyCapturedState(
  Human: HumanInterface,
  capture: SceneIO.SceneCapture,
  store: Store,
  isLegacyCapture = false,
) {
  return new Promise((resolve) => {
    // handle selecting and hiding objects
    const viewGraph = store.selectSnapshot(EngineState.getViewGraph);

    const checkedNodes = capture.objects
      .filter((object) => object.selected === true)
      .map((object) => object.objectId);

    // handle old capture format, where objects that are hidden are EXCLUDED from capture.objects
    // HACK the old format removed objects from the capture if they were hidden
    // so, if an object in the scene is missing from the capture, it should be toggled off.
    const hiddenNodes: ObjectId[] = (() => {
      if (isLegacyCapture) {
        const hiddenNodesLegacy: ObjectId[] = Human.scene.getLeaves().filter((objectId) => {
          const captureObject = capture.objects.find((object) => object.objectId === objectId);
          const missingFromCapture = captureObject === undefined;
          return missingFromCapture;
        });
        return hiddenNodesLegacy;
      } else {
        return capture.objects
          .filter((object) => object.hidden === true)
          .map((object) => object.objectId);
      }
    })();

    // handle background model for each chapter
    const setBackgroundActions = capture.background.map(
      (backgroundCapture) =>
        new SetChapterBackground(backgroundCapture.chapterId, backgroundCapture.background),
    );

    const selectChapterAction = capture.activeChapterId
      ? [new SelectChapterById(capture.activeChapterId)]
      : [];

    const zoomSet =
      capture.camera.zoom !== undefined ? [new ZoomCameraStatic(capture.camera.zoom)] : [];

    // set Timeout, so that nodes are highlighted
    setTimeout(() => {
      store.dispatch([
        new SetCamera(capture.camera.lookAt, false),
        new SetLabels(capture.labels),
        new NodeActions.UpdateNodesShown(false, hiddenNodes),
        new NodeActions.UpdateNodesChecked(true, checkedNodes),
        ...zoomSet,
        ...selectChapterAction,
        ...setBackgroundActions,
      ]);
      resolve(undefined);
    });
  });
}

function restoreScene(
  Human: HumanInterface,
  capture: SceneIO.SceneCapture,
  store: Store,
  engineService: EngineService,
  isLegacyCapture: boolean,
): Observable<true> {
  return new Observable((observer) => {
    engineService
      .getEvent(EventsEnum.HumanStarted)
      .pipe(first())
      .subscribe(() => {
        applyCapturedState(Human, capture, store, isLegacyCapture).then(() => {
          engineService.fireEvent(EventsEnum.SceneRestored, undefined);
          observer.next(true);
          observer.complete();
        });
      });
    loadStory(store, capture.storyId);
  });
}

function focusObject(
  params: SceneIO.FocusObjectRequest,
  store: Store,
  engineService: EngineService,
) {
  // enable xray mode if something is selected
  if (params.select && params.xray) {
    enableXrayMode(store, engineService);
  } else {
    disableXrayMode(store, engineService);
  }

  // set selected
  if (params.select) {
    store.dispatch(new NodeActions.UpdateNodesChecked(true, [params.objectId]));
  }

  // flyTo object
  const animate = true;
  const animationDuration = 1;
  store.dispatch(new FlyToObject(params.objectId, animate, animationDuration));
}

function enableXrayMode(store: Store, engineService: EngineService): true {
  store.dispatch(new SetXrayMode(true));
  engineService.fireEvent(EventsEnum.SceneXrayEnabled, undefined);
  return true;
}

export function getSceneEvents(): RPCEvent<SceneIO.SceneEventResponse>[] {
  return [
    {
      eventName: EventsEnum.SceneLoaded,
      mapFn: (e) => e,
    },
    {
      eventName: EventsEnum.ScenePicked,
      aliasEvents: [EventsEnum.PickPicked],
      mapFn: formatPickResult,
    },
    {
      eventName: EventsEnum.SceneObjectsSelected,
      mapFn: (event) => event.selectedObjectsUpdate,
    },
    {
      eventName: EventsEnum.SceneObjectsShown,
      mapFn: (event) => event.enabledObjectsUpdate,
    },
    {
      eventName: EventsEnum.SceneReady,
      aliasEvents: [EventsEnum.Started],
    },
    {
      eventName: EventsEnum.SceneRestored,
      mapFn: () => true,
    },
    {
      eventName: EventsEnum.SceneXrayEnabled,
      mapFn: () => true,
    },
    {
      eventName: EventsEnum.SceneXrayDisabled,
      mapFn: () => true,
    },
    {
      eventName: EventsEnum.NativeShare,
      mapFn: (data: SceneIO.SceneNativeShareEvent) => data,
    },
    {
      eventName: EventsEnum.NativeDebugMessage,
      mapFn: (message: SceneIO.SceneNativeDebugMessage) => message,
    },
  ];
}

export function getSceneProcedures(
  Human: HumanInterface,
  store: Store,
  engineService: EngineService,
  paintService: PaintService,
  cameraService: CameraService,
  graphService: BdGraphService,
  animationService: AnimationService,
  viewService: ViewService,
  visibilityLayersService: VisibilityLayersService,
): Procedure<SceneIO.SceneProcRequest, SceneIO.SceneProcResponse>[] {
  return [
    {
      // used by SDK
      procName: MessageEnum.SceneMetaData,
      actionFn: () => {
        const story = store.selectSnapshot(StoryState.getStory);
        const chapters = store.selectSnapshot(StoryChaptersState.getStoryChapters);
        return {
          title: story.displayName,
          description: story.description,
          chapters: chapters.map((c) => {
            return {
              id: c.id,
              title: c.info.displayName,
              description: c.info.description,
              isAnimated: animationService.isChapterAnimated(c),
              loops: animationService.isChapterLooped(c),
              duration: animationService.getChapterLocalTimerange(c.id),
              media: SceneAPIUtils.convertMediaFormat(c.media),
            };
          }),
        };
      },
    },
    {
      // legacy, keeping support for now
      procName: MessageEnum.focusObject,
      actionFn: (params: SceneIO.FocusObjectRequest) => {
        focusObject(params, store, engineService);
        return true;
      },
      hideDefinition: true,
    },
    {
      procName: MessageEnum.dissectSetEnabled,
      actionFn: (params: SceneIO.DissectSetEnabledRequest) => {
        const tool = params.enable ? ToolEnum.Hide : ToolEnum.Select;
        store.dispatch(new SetActiveTool(tool));
        return true;
      },
      hideDefinition: true,
    },
    {
      procName: MessageEnum.sceneSetClickMode,
      actionFn: (mode: SceneIO.SceneSetClickModeRequest) => {
        const tool = (() => {
          if (mode === 'select') {
            return ToolEnum.Select;
          }
          if (mode === 'hide') {
            return ToolEnum.Hide;
          }
          if (mode === 'fade') {
            return ToolEnum.Fade;
          }
          return ToolEnum.Select;
        })();

        store.dispatch(new SetActiveTool(tool));
        return true;
      },
    },
    {
      procName: MessageEnum.sceneGetClickMode,
      actionFn: () => {
        return store.selectSnapshot(EngineState.getActiveTool);
      },
    },
    {
      procName: MessageEnum.pickSingleSetEnabled,
      actionFn: (params: SceneIO.PickSingleSetEnabledRequest) => {
        const enable = (() => {
          switch (params.enable) {
            case undefined:
              return !store.selectSnapshot(ViewState.getMultiPickModeEnabled);
            case true:
              return false;
            case false:
              return true;
          }
        })();
        store.dispatch(new ViewActions.SetMultiPickModeEnabled(enable));
        return true;
      },
      hideDefinition: true,
    },
    {
      procName: MessageEnum.pickMultiSetEnabled,
      actionFn: (params: SceneIO.PickMultiSetEnabledRequest) => {
        const enable = (() => {
          switch (params.enable) {
            case undefined:
              return !store.selectSnapshot(ViewState.getMultiPickModeEnabled);
            default:
              return params.enable;
          }
        })();
        store.dispatch(new ViewActions.SetMultiPickModeEnabled(enable));
        return true;
      },
      hideDefinition: true,
    },
    {
      procName: MessageEnum.getCanvasPos,
      actionFn: (params: SceneIO.SceneGetCanvasPosRequest) => {
        const canvas = engineService.getCanvas();
        return _3DUtils.getCanvasPos(
          Human,
          params.worldPos,
          params.offset,
          canvas.width,
          canvas.height,
        );
      },
      hideDefinition: true,
    },
    {
      procName: MessageEnum.SceneCapture,
      actionFn: () => captureScene(Human, store, cameraService),
    },
    {
      procName: MessageEnum.SceneRestore,
      actionFn: (capture: SceneIO.SceneRestoreRequest) => {
        const isLegacyCapture = SceneAPIUtils.isLegacyCaptureFormat(capture);

        const sanitizedCapture = isLegacyCapture
          ? SceneAPIUtils.convertCaptureFormat(capture as SceneIO.LegacySceneCapture)
          : (capture as SceneIO.SceneCapture);

        return restoreScene(Human, sanitizedCapture, store, engineService, isLegacyCapture);
      },
    },
    {
      procName: MessageEnum.SceneEnableXrayMode,
      actionFn: () => enableXrayMode(store, engineService),
    },
    {
      procName: MessageEnum.SceneDisableXrayMode,
      actionFn: () => disableXrayMode(store, engineService),
    },
    {
      procName: MessageEnum.SceneLoad,
      actionFn: (id: SceneIO.SceneLoadRequest) => {
        loadStory(store, id);
        return true;
      },
    },
    {
      procName: MessageEnum.SceneSetBaseDirectory,
      actionFn: (dir: SceneIO.SceneSetBaseDirectoryRequest) => {
        Human.events.fire('scene.setBaseDirectory', dir);
        return true;
      },
    },
    {
      procName: MessageEnum.NativeSetSdkOffline,
      actionFn: (value: SceneIO.SceneNativeSetSdkOffline) => {
        Human.events.fire('native.setSdkOffline', value);
        return true;
      },
    },
    {
      procName: MessageEnum.SceneReset,
      actionFn: () => {
        viewService.resetChapter();
        return true;
      },
    },
    {
      procName: MessageEnum.SceneInfo,
      actionFn: (params: SceneIO.SceneInfoRequest) => {
        const objectIds =
          params && params.objectId ? [params.objectId] : Object.keys(Human.scene.objects);

        const displayName = store.selectSnapshot(StoryState.getStory).displayName;
        return buildSceneInfoResponse(Human, displayName, objectIds, store);
      },
    },
    {
      procName: MessageEnum.ScenePick,
      actionFn: (params: SceneIO.ScenePickRequest) => {
        // TODO[AC]: queryPick picking (don't trigger event) not exposed in Studio engine
        const triggerActions = params.triggerActions !== false;

        const pick: PickModel = {
          x: params.x,
          y: params.y,
          rayPick: true,
        };

        const hit = engineService.pickScene(pick);
        return formatPickResult(hit);
      },
    },
    {
      procName: MessageEnum.SceneSelectObjects,
      actionFn: (params: SceneIO.SelectObjectsRequest) => {
        // sanitize against the deprecated 'replace' param
        const shouldReplace = params['replace'] === true;
        const sanitizedParams = params;
        delete sanitizedParams['replace'];

        if (shouldReplace) {
          store.dispatch(new NodeActions.UpdateNodesChecked(false));
        }

        store.dispatch(new NodeActions.UpdateNodesChecked(false, [], sanitizedParams));
        return true;
      },
    },
    {
      procName: MessageEnum.SceneShowObjects,
      actionFn: (params: SceneIO.ShowObjectsRequest) => {
        // sanitize against the deprecated 'replace' param
        delete params['replace'];

        // Upstream is now the source of truth
        // Child node state needs to be calculated from a patch,
        // instead of relying on EngineService.setNodesVisible to produce the correct result.

        const calculateShownPatch = (objectId: string, value: boolean) => {
          const immediateChildren = Human.scene.objects[objectId].objects;

          let _patch = { [objectId]: value };

          for (const child of immediateChildren) {
            const inPayload = params[child.objectId] !== undefined;

            if (inPayload) {
              continue;
            }

            // otherwise, save its value and recurse.
            _patch[child.objectId] = value;
            _patch = { ..._patch, ...calculateShownPatch(child.objectId, value) };
          }

          return _patch;
        };

        const patch = Object.entries(params).reduce((acc, [objectId, value]) => {
          return { ...acc, ...calculateShownPatch(objectId, value) };
        }, {});

        store.dispatch(new NodeActions.UpdateNodesShown(false, [], patch));
        return true;
      },
    },
    {
      // only included to support migration away from scene.selectionMode
      procName: MessageEnum.IsolateObject,
      actionFn: (objectIds: SceneIO.SceneIsolateObjectRequest) => {
        const nodeIds = typeof objectIds === 'string' ? [objectIds] : objectIds;
        const viewGraph = store.selectSnapshot(EngineState.getViewGraph);
        const inverseNodeIds = graphService.getInverseSelection(viewGraph, nodeIds);
        const nodes = graphService.getNodes(viewGraph, inverseNodeIds);

        store.dispatch(
          new NodeActions.UpdateNodesShown(
            false,
            nodes.map((n) => n.id),
          ),
        );

        return true;
      },
    },
    {
      procName: MessageEnum.SceneTransformObject,
      actionFn: (params: SceneIO.TransformObjectRequest) => {
        engineService.transformObject(params.objectId, params);
        return true;
      },
    },
    {
      procName: MessageEnum.SceneColorObject,
      actionFn: (params: SceneIO.ColorObjectRequest) => {
        const { objectId, tintColor, saturation, contrast, brightness, opacity } = params;

        const colorRecipe: EngineColorRecipeModel = {
          tintColor,
          saturation,
          contrast,
          brightness,
          opacity,
        };

        store.dispatch(
          new NodeActions.UpdateNodesPaint(ColorRecipeModel.fromEngine(colorRecipe), [objectId]),
        );

        return true;
      },
    },
    {
      procName: MessageEnum.SceneGetColor,
      actionFn: (params: SceneIO.SceneColorRequest) => {
        const recipe = store.selectSnapshot(EngineNodesState.getNodePaint)(params.objectId);
        return ColorRecipeModel.toEngine(recipe);
      },
    },
    {
      procName: MessageEnum.SceneUncolorObject,
      actionFn: (params: SceneIO.SceneColorRequest) => {
        store.dispatch(new NodeActions.UpdateNodesPaint(PaintPresetOriginal, [params.objectId]));
        return true;
      },
    },
    {
      procName: MessageEnum.SceneDisableHighlight,
      actionFn: () => {
        paintService.disableHighlight();
        return true;
      },
    },
    {
      procName: MessageEnum.SceneEnableHighlight,
      actionFn: () => {
        paintService.enableHighlight();
        return true;
      },
    },
    {
      procName: MessageEnum.SceneSetHighlightColor,
      actionFn: (params: SceneIO.SceneSetHighlightRequest) => {
        paintService.setHighlightColor(params);
        return true;
      },
    },
    {
      procName: MessageEnum.SceneSetLayersSliderPosition,
      actionFn: (params: SceneIO.SceneSetLayersSliderPositionRequest) => {
        const fraction = Math.min(1, Math.max(params.position, 0));

        const index = visibilityLayersService.fractionToIndex(fraction);
        const position = visibilityLayersService.fractionToPosition(fraction, index);

        if (params.record) {
          visibilityLayersService.recordLayerIndexPosition(index, position);
        } else {
          visibilityLayersService.synchronizeScene(index, position, false);
        }

        return true;
      },
    },
    {
      procName: MessageEnum.SceneGetLayers,
      actionFn: () => {
        return store.selectSnapshot(StoryChaptersState.getAllChaptersCurrentLayers);
      },
    },
  ];
}
